// src/components/MenteeEngagementHeatmap.js
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

// Kanonische Kriterien, die wir in der Heatmap zeigen wollen
const CANONICAL_CRITERIA = ['reflection', 'trust', 'communication', 'engagement'];

// Synonyme / unterschiedliche Key-Schreibweisen, die aus dem Backend kommen können
const CRITERION_SYNONYMS = {
  reflection: ['reflection', 'Reflexion'],
  trust: ['trust', 'Vertrauen'],
  communication: ['communication', 'Kommunikation'],
  engagement: ['engagement', 'Engagement', 'criterion_Engagement'],
};

const MenteeEngagementHeatmap = ({ surveyId = null }) => {
  const { t } = useTranslation();

  const [matrix, setMatrix] = useState(null);
  const [criticalRows, setCriticalRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const milestoneLabels = {
    kennenlerntermin: t('milestoneIntro'),
    '3mon': t('milestone3'),
    '6mon': t('milestone6'),
    '9mon': t('milestone9'),
    '12mon': t('milestone12'),
  };

  const orderedMilestones = ['kennenlerntermin', '3mon', '6mon', '9mon', '12mon'];

  const surveyParams = useMemo(() => {
    const n = Number(surveyId);
    if (Number.isInteger(n) && n > 0) {
      return { surveyId: n, survey_id: n };
    }
    return undefined;
  }, [surveyId]);

  const [reviewCount, setReviewCount] = useState(0);

  // ✅ NEU: Backend kann entweder {matrix, critical} liefern ODER ein Array wie im DevTools-Screenshot.
  const buildMatrixFromRows = (rows) => {
    const mtx = {};
    for (const m of orderedMilestones) mtx[m] = {};

    for (const r of rows) {
      const criterionName = r?.criterion;
      if (!criterionName) continue;

      for (const m of orderedMilestones) {
        if (Object.prototype.hasOwnProperty.call(r, m)) {
          // raw bleibt raw (String/Number/null). Parsing passiert in getValueForCell
          mtx[m][criterionName] = r[m];
        }
      }
    }

    return mtx;
  };

  // Daten für Heatmap + kritische Bewertungen laden
  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      setLoading(true);

      try {
        const res = await axios.get('/mentoring/mentee-engagement-avg-heatmap', {
          withCredentials: true,
          params: surveyParams,
        });

        if (!isMounted) return;

        const payload = res.data;

        // Case A: neues Backend-Format (Array von Zeilen)
        if (Array.isArray(payload)) {
          setMatrix(buildMatrixFromRows(payload));
          setCriticalRows([]); // Details kommen nur, wenn Backend sie liefert
          // Extract count from first row if available
          const cnt = payload.length > 0 ? (Number(payload[0].count) || 0) : 0;
          setReviewCount(cnt);
          return;
        }

        // Case B: altes/erwartetes Format ({matrix, critical})
        if (payload && typeof payload === 'object') {
          if (payload.matrix) {
            setMatrix(payload.matrix || null);
          } else if (Array.isArray(payload.rows)) {
            // optionaler Fallback, falls Backend {rows:[...]} liefert
            setMatrix(buildMatrixFromRows(payload.rows));
          } else {
            setMatrix(null);
          }

          setCriticalRows(Array.isArray(payload.critical) ? payload.critical : []);
          setReviewCount(Number(payload.respondent_count || payload.count) || 0);
          return;
        }

        // Unbekanntes Format
        setMatrix(null);
        setCriticalRows([]);
        setReviewCount(0);
      } catch (err) {
        if (!isMounted) return;
        console.error('Fehler beim Laden der Engagement-Heatmap:', err);
        setMatrix(null);
        setCriticalRows([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    load();

    return () => {
      isMounted = false;
    };
  }, [surveyParams]); // absichtlich nur surveyParams

  // Sichtbare Meilensteine bestimmen (nur, wenn irgendwo Werte vorliegen)
  const { visibleMilestones } = useMemo(() => {
    if (!matrix) {
      return { visibleMilestones: [] };
    }

    const milestones = [];

    for (const m of orderedMilestones) {
      const row = matrix[m];
      if (!row) continue;

      const hasAnyValue = CANONICAL_CRITERIA.some((criterionKey) => {
        const synonyms = CRITERION_SYNONYMS[criterionKey] || [criterionKey];
        return synonyms.some((name) => {
          const val = row[name];
          return val !== undefined && val !== null && val !== '';
        });
      });

      if (hasAnyValue) {
        milestones.push(m);
      }
    }

    return { visibleMilestones: milestones };
  }, [matrix]);

  // Helfer: Wert für eine Zelle holen (inkl. Synonym-Behandlung)
  const getValueForCell = (milestoneKey, criterionKey) => {
    if (!matrix) return null;
    const row = matrix[milestoneKey] || {};
    const synonyms = CRITERION_SYNONYMS[criterionKey] || [criterionKey];

    for (const name of synonyms) {
      if (Object.prototype.hasOwnProperty.call(row, name)) {
        const raw = row[name];
        if (raw === 0) return 0;
        if (raw === null || raw === undefined || raw === '') return null;

        const num = Number(raw);
        return Number.isNaN(num) ? null : num;
      }
    }
    return null;
  };

  const criticalByMentor = useMemo(() => {
    const map = new Map();

    for (const row of criticalRows) {
      const mentorId = row.mentor_user_id || row.mentor_id;
      if (!mentorId) continue;

      if (!map.has(mentorId)) {
        map.set(mentorId, {
          mentor_id: mentorId,
          mentor_first: row.first_name,
          mentor_last: row.last_name,
          items: [],
        });
      }

      map.get(mentorId).items.push({
        mentee_first: row.mentee_first,
        mentee_last: row.mentee_last,
        criterion: row.criterion,
        milestone: row.milestone,
        score: row.score,
      });
    }

    return Array.from(map.values());
  }, [criticalRows]);

  const criticalCount = criticalRows.length;

  const getCellClasses = (score) => {
    if (score == null) {
      return 'bg-gray-700 text-gray-500';
    }
    if (score >= 4) {
      return 'bg-green-500 text-white';
    }
    if (score === 3) {
      return 'bg-yellow-400 text-gray-900';
    }
    return 'bg-red-600 text-white';
  };

  // Chat (HR → Mentor)
  const openChatWithMentor = (mentorId, firstName, lastName) => {
    const presetMessage =
      `Hallo ${firstName || ''} ${lastName || ''}`.trim() +
      ', ich habe mir dein Feedback zum Engagement deiner Mentees angeschaut ' +
      'und würde gerne kurz mit dir darüber sprechen.';

    window.dispatchEvent(
      new CustomEvent('openChatPopup', {
        detail: {
          menteeId: null,
          mentorId,
          senderRole: 'hr',
          receiverRole: 'mentor',
          presetMessage,
          context: {
            source: 'mentee-engagement',
            forceAppHandling: true,
          },
        },
      })
    );
  };

  const criteriaList = CANONICAL_CRITERIA;

  return (
    <>
      <div className="bg-gray-800 rounded-2xl p-6 shadow-md relative">
        {/* Titel + Badge */}
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-white text-lg font-bold">
            {t('menteeEngagementTitle') || 'Engagement der Mentees (Mentorensicht)'}
          </h3>

          {criticalCount > 0 && (
            <button
              type="button"
              onClick={() => setShowModal(true)}
              className="flex items-center gap-2 bg-red-600 text-white text-xs font-semibold px-3 py-1 rounded-full shadow hover:bg-red-500 transition"
            >
              <span className="bg-white text-red-600 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold">
                {criticalCount}
              </span>
              <span>{t('criticalEngagementLabel') || 'kritische Bewertung(en)'}</span>
            </button>
          )}
        </div>

        {/* Heatmap-Inhalt */}
        {loading ? (
          <div className="flex items-center justify-center h-64 text-gray-300 text-sm">
            {t('loading') || 'Lade Daten…'}
          </div>
        ) : !matrix || visibleMilestones.length === 0 ? (
          <div className="flex items-center justify-center h-64 text-gray-400 text-sm">
            {t('noData') || 'Noch keine Feedbackdaten vorhanden.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm text-left text-gray-200">
              <thead>
                <tr>
                  <th className="px-4 py-2 text-xs font-semibold text-gray-300">
                    {t('engagementCriterionHeader') || 'Kriterium'}
                  </th>
                  {visibleMilestones.map((m) => (
                    <th key={m} className="px-4 py-2 text-xs font-semibold text-gray-300">
                      {milestoneLabels[m] || m}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {criteriaList.map((criterion) => (
                  <tr key={criterion} className="border-t border-gray-700">
                    <td className="px-4 py-2 text-sm text-gray-200">
                      {t(`criterion_${criterion}`) || criterion}
                    </td>
                    {visibleMilestones.map((m) => {
                      const value = getValueForCell(m, criterion);
                      return (
                        <td key={m} className="px-4 py-2">
                          <div
                            className={`w-8 h-8 rounded-md flex items-center justify-center text-sm font-semibold ${getCellClasses(
                              value
                            )}`}
                          >
                            {value != null ? value : '–'}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && reviewCount > 0 && (
          <p className="text-xs text-gray-400 mt-2 text-right">
            {reviewCount === 1
              ? t('basedOnOneReview', 'Basierend auf 1 Bewertung')
              : t('basedOnReviews', 'Basierend auf {{count}} Bewertungen', { count: reviewCount })}
          </p>
        )}
      </div>

      {/* Modal mit kritischen Bewertungen – pro Mentor gruppiert */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
          <div className="bg-gray-900 rounded-2xl p-6 w-full max-w-4xl shadow-xl max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h4 className="text-white text-lg font-bold">
                {t('showCriticalEngagement') || 'Kritische Bewertungen anzeigen'}
              </h4>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="text-gray-300 hover:text-white text-sm px-2 py-1"
              >
                ✕
              </button>
            </div>

            {criticalByMentor.length === 0 ? (
              <div className="text-gray-300 text-sm">
                {t('noCriticalEngagement') ||
                  'Aktuell keine kritischen Bewertungen vorhanden.'}
              </div>
            ) : (
              <div className="space-y-4">
                {criticalByMentor.map((m) => (
                  <div
                    key={m.mentor_id}
                    className="border border-gray-700 rounded-xl p-4 bg-gray-800"
                  >
                    <div className="flex justify-between items-center mb-2">
                      <div>
                        <div className="text-white font-semibold">
                          {m.mentor_first} {m.mentor_last}
                        </div>
                        <div className="text-gray-400 text-xs">
                          {t('criticalEngagementSubtitle') ||
                            'Kritische Bewertungen zu betreuten Mentees.'}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          openChatWithMentor(m.mentor_id, m.mentor_first, m.mentor_last)
                        }
                        className="text-xs bg-green-500 hover:bg-green-400 text-white font-semibold px-3 py-1 rounded-full shadow"
                      >
                        Nachricht an Mentor
                      </button>
                    </div>

                    <ul className="text-gray-200 text-xs space-y-1">
                      {m.items.map((it, idx) => (
                        <li key={`${m.mentor_id}-${idx}`}>
                          <span className="font-semibold">
                            {it.mentee_first} {it.mentee_last}
                          </span>{' '}
                          – {t(`criterion_${it.criterion}`) || it.criterion} –{' '}
                          {milestoneLabels[it.milestone] || it.milestone} ·{' '}
                          <span className="font-semibold">{it.score}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="bg-gray-700 hover:bg-gray-600 text-white text-sm font-medium px-4 py-2 rounded-lg"
              >
                {t('close') || 'Schließen'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MenteeEngagementHeatmap;
